import {
  Box,
  Button,
  Card,
  Dialog,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import InputMask from 'react-input-mask';
import { useFormik } from 'formik';
import CustomModal, {
  CustomModalBasicProps,
  GridItem,
  GridSection,
} from 'src/components/CustomModal';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import {
  ContactPoint,
  ContactPointUse,
  Extension,
  PatientGender,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import DatePickerMoment from 'src/components/DatePickerMoment';
import moment from 'moment';
import * as yup from 'yup';
import GenderIdentityAutoComplete from 'src/components/GenderIdentityAutoComplete';
import { usePatients } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { Add } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { getGenderIdentityExtension, getIdentifier } from 'src/utils/fhir';
import { IdentifierCode, PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { SimpleOrgI } from 'src/careflow/ph-core/authentication/context/JWTContext';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { getUUID } from 'src/utils/url';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useSnackbar } from 'notistack';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import AddAddressLoading from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/AddAddressLoading';
import { capitalize } from 'src/utils/string';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { MpiService } from 'src/careflow/ph-core/mpi/services';

export interface AddPatientModalProps extends CustomModalBasicProps {}

export interface PatientForm {
  firstName: string;
  lastName: string;
  middleName: string;
  dateOfBirth: moment.Moment | null;
  gender: string;
  genderIdentity: ValueSetComposeIncludeConcept | null;
  emailType: string;
  email: string;
  phoneType: string;
  phone: string;
  areaCode: string;
  street: string;
  street2: string;
  city: string;
  district: string;
  state: string;
  zipCode: string;
  housingStatus: string;
}

const initialPatientForm: PatientForm = {
  firstName: '',
  lastName: '',
  middleName: '',
  dateOfBirth: null,
  gender: '',
  genderIdentity: null,
  email: '',
  emailType: '',
  phoneType: '',
  phone: '',
  areaCode: '',
  street: '',
  street2: '',
  city: '',
  district: '',
  state: '',
  zipCode: '',
  housingStatus: ''
};

const AddPatientModal = ({ onClose, ...modalProps }: AddPatientModalProps) => {
  const { i18n } = useLocales();
  const navigate = useNavigate();

  const isAllowedAddressHousingStatus = 
    checkAclValidation({ acls: [crsAcls.CRS.PATIENT.ADDRESS_DETAILS.ADD_HOUSING_STATUS] })

  const {
    valueSets: [patientIdentifierTypes, genderIdentityVS, housingStatusVS],
    isLoading: isLoadingVS
  } = useValueSetsByIdentifiers([
    'patient-identifier-types','ph-gender-identity', 'udsplus-housing-status-codes']);

  const [openAddAddress, setOpenAddAddress] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const [externalError, setExternalError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [, { create }] = usePatients({ map: PatientWrapper });

  const { enqueueSnackbar } = useSnackbar();

  const auth = useAuth();
  const { configurations, componentsData } = useTenantConfigData();
  const { patient: patientConfig } = componentsData;

  const selectedOrg = useMemo<SimpleOrgI>(() => auth.getSelectedOrg(), [auth]);

  const validationSchema = yup.object({
    firstName: yup
      .string()
      .required(i18n('patients.addPatient.validationMessages.firstName.isRequired', 'crs'))
      .matches(
        /^[aA-zZ\s]+$/,
        i18n('patients.addPatient.validationMessages.firstName.onlyAlphabet', 'crs')
      )
      .max(25, i18n('patients.addPatient.validationMessages.firstName.maxLength', 'crs')),
    lastName: yup
      .string()
      .required(i18n('patients.addPatient.validationMessages.lastName.isRequired', 'crs'))
      .matches(
        /^[a-zA-Z\s-]+$/,
        i18n('patients.addPatient.validationMessages.lastName.onlyAlphabet', 'crs')
      )
      .max(25, i18n('patients.addPatient.validationMessages.lastName.maxLength', 'crs')),
    middleName: yup
      .string()
      .matches(
        /^[aA-zZ\s]+$/,
        i18n('patients.addPatient.validationMessages.middleName.onlyAlphabet', 'crs')
      )
      .max(25, i18n('patients.addPatient.validationMessages.middleName.maxLength', 'crs')),
    gender: yup
      .string()
      .required(i18n('patients.addPatient.validationMessages.gender.isRequired', 'crs')),
    dateOfBirth: yup
      .mixed()
      .required(i18n('patients.addPatient.validationMessages.dateOfBirth.isRequired', 'crs'))
      .test(
        'validDate',
        i18n('patients.addPatient.validationMessages.dateOfBirth.validDate', 'crs'),
        function (value) {
          const date = moment(value ?? null);
          return date.isValid();
        }
      )
      .test(
        'CantBeFuture',
        i18n('patients.addPatient.validationMessages.dateOfBirth.cantBeFuture', 'crs'),
        function (value) {
          const date = moment(value ?? null);
          if (!date.isValid()) return true;
          return date.isSameOrBefore(moment(), 'date');
        }
      ),
    email: yup.string().email('Email must be a valid email address'),
    emailType: yup.string().when('email', (email, schema) => {
      if (email) return schema.required('Email Type is required');
    }),
    areaCode: yup
      .string()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .matches(/^[(]?[0-9]{3}[)]$/, 'Area Code must be 3 numbers')
      .when('phone', (value, schema) => {
        if (value) return schema.required('Area Code is required');
      }),
    phone: yup
      .string()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .matches(/^[0-9]{3}[-\s\.]?[0-9]{4,6}$/, 'Phone Number must be 7 numbers'),
    phoneType: yup.string().when('phone', (phone, schema) => {
      if (phone) return schema.required('Phone Type is required');
    }),
    housingStatus: yup.string()
  });

  const handleMPISearch = async (
    dataForm: PatientForm,
    create_if_not_exist: boolean
  ): Promise<any> => {
    const payload = {
      name: [{ family: dataForm.lastName, given: [dataForm.firstName] }],
      birthDate: dataForm.dateOfBirth?.isValid?.() 
        ? dataForm.dateOfBirth.startOf('day').toISOString().split('T')[0]
        : undefined,
      gender: dataForm.gender as PatientGender,
      createIfNotExist: create_if_not_exist,
    };

    const res = await MpiService.search(payload);
    const data = res?.[0];

    return data;
  };

  const onSubmit = async (data: PatientForm) => {
    try {
      setIsLoading(true);
      const mpiSearchResult = await handleMPISearch(data, false);

      if (isEmpty(mpiSearchResult)) {
        const dataResult = await handleMPISearch(data, true);

        if (!isEmpty(dataResult)) {
          dataResult?.identifier?.push({
            use: 'official',
            type: 'master_patient_uuid',
            value: dataResult?.uuid,
            system: `${configurations?.mpi?.searchUrl}/patients`,
          });

          const mrn = getIdentifier(dataResult, IdentifierCode.MEDICAL_RECORD_NUMBER.toLowerCase(), patientIdentifierTypes, "MR" );
          const master_person_uuid = getIdentifier(dataResult, IdentifierCode.MASTER_PERSON_UUID.toLowerCase(), patientIdentifierTypes);
          const master_patient_uuid = getIdentifier(dataResult, IdentifierCode.MASTER_PATIENT_UUID.toLowerCase(), patientIdentifierTypes);

          const telecom: ContactPoint[] = [];

          if(!isEmpty(address)){
            if(isEmpty(address?.extension)) {
              address.extension = [];
            }
  
            if(data.housingStatus){
              const housingStatusExtension = {
                url: housingStatusVS?.getConceptSystem(),
                valueCode: data.housingStatus
              };
              address.extension.push(housingStatusExtension);
            }
          }

          if (!isEmpty(data.emailType) && !isEmpty(data.email))
            telecom.push({
              system: 'email',
              use: data.emailType as ContactPointUse,
              value: data.email,
            });
          if (!isEmpty(data.phoneType) && !isEmpty(data.phone)) {
            telecom.push({
              system: 'phone',
              use: data.phoneType as ContactPointUse,
              ...(data.areaCode
                ? { value: `${data.areaCode} ${data.phone}` }
                : { value: `${data.phone}` }),
            });
          }

          const genderIdentity = data.genderIdentity;

          const genderIdentityExtension: Extension | null =
            genderIdentity?.code && genderIdentity?.display
              ? getGenderIdentityExtension({
                  ...genderIdentity,
                  system: genderIdentityVS?.getConceptSystem?.(),
                },
                configurations?.fhir
              )
              : null;

          if (!isEmpty(mrn)) {
            const [fhirPatient] = await create({
              resourceType: 'Patient',
              active: true,
              identifier: [mrn, master_person_uuid, master_patient_uuid].filter(Boolean),
              name: [
                {
                  given: [data.firstName, ...(!isEmpty(data.middleName) ? [data.middleName] : [])],
                  family: data.lastName,
                },
              ],
              birthDate: data.dateOfBirth?.isValid?.()
                ? data.dateOfBirth.toISOString().split('T')[0]
                : undefined,
              ...(data.gender && { gender: data.gender as PatientGender }),
              ...(!isEmpty(telecom) && { telecom: telecom }),
              ...(!isEmpty(address) && { address: [address] }),
              managingOrganization: {
                display: selectedOrg?.name,
                reference: `Organization/${getUUID(selectedOrg?.fhir_uri)}`,
              },
              extension: genderIdentityExtension ? [genderIdentityExtension] : undefined,
            });

            navigate(PATH_DASHBOARD.crs.patient.details.forId(fhirPatient.id || ''));
          }
        }
      } else {
        enqueueSnackbar('This patient was already registered', { variant: 'error' });
      }

      formik.resetForm();

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar('An error has ocurred.', { variant: 'error' });
    }
  };
  const formik = useFormik<PatientForm>({
    initialValues: initialPatientForm,
    validationSchema,
    onSubmit,
  });

  const setAddressFields = async (data: any) => {

    if( 
      patientConfig?.address?.requiredHousingStatus && 
      isAllowedAddressHousingStatus &&
      isEmpty(formik.values.housingStatus)
    ){
      setExternalError(true);
      return;
    }

    formik.setFieldValue('street', data.line[0]);
    formik.setFieldValue('street2', data.line[1]);
    formik.setFieldValue('city', data.city);
    formik.setFieldValue('district', data.district);
    formik.setFieldValue('zipCode', data.postalCode);
    formik.setFieldValue('state', data.state);
    setAddress(data);
    setOpenAddAddress(false);
    setExternalError(false);
  };

  const handlerClose = () => {
    setOpenAddAddress(false);
    setExternalError(false);
    formik.setFieldValue('housingStatus', '');
    setAddress(null);
  };

  const handlerCloseCustomModal = () => {
    if (onClose) {
      onClose({}, "backdropClick");
    }
    handlerClose();
    formik.resetForm();
  };

  return (
    <>
      <CustomModal
        {...modalProps}
        title={i18n('patients.list.addPatient', 'crs')}
        breadcrumbs={[i18n('patients.list.title', 'crs'), i18n('patients.list.addPatient', 'crs')]}
        isLoading={isLoading}
        onSave={() => {
          formik.handleSubmit();
          if (Object.entries(formik.errors).length) return;
        }}
        onCancel={handlerCloseCustomModal as Function}
        onClose={handlerCloseCustomModal}
      >
        <Box sx={{ my: 3, width: '100%' }}>
          <Grid container sx={{ width: '100%' }} my={3}>
            <Grid item xs={12}>
              <GridSection mt={0}>
                <GridItem>
                  <TextField
                    fullWidth
                    id="firstName"
                    name="firstName"
                    label={`${i18n('patients.firstName', 'crs')}*`}
                    placeholder={`${i18n('patients.firstName', 'crs')}*`}
                    error={formik.touched.firstName && !!formik.errors.firstName}
                    helperText={formik.touched.firstName && formik.errors.firstName}
                    variant="outlined"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                  />
                </GridItem>
                <GridItem>
                  <TextField
                    fullWidth
                    id="middleName"
                    name="middleName"
                    label={i18n('patients.middleName', 'crs')}
                    placeholder={i18n('patients.middleName', 'crs')}
                    error={formik.touched.middleName && !!formik.errors.middleName}
                    helperText={formik.touched.middleName && formik.errors.middleName}
                    variant="outlined"
                    value={formik.values.middleName}
                    onChange={formik.handleChange}
                  />
                </GridItem>
              </GridSection>
              <GridSection>
                <GridItem>
                  <TextField
                    fullWidth
                    id="lastName"
                    name="lastName"
                    label={`${i18n('patients.lastName', 'crs')}*`}
                    placeholder={`${i18n('patients.lastName', 'crs')}*`}
                    error={formik.touched.lastName && !!formik.errors.lastName}
                    helperText={formik.touched.lastName && formik.errors.lastName}
                    variant="outlined"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                  />
                </GridItem>
                <GridItem>
                  <DatePickerMoment
                    value={formik.values.dateOfBirth}
                    error={
                      formik.touched.dateOfBirth && formik.errors.dateOfBirth
                        ? formik.errors.dateOfBirth
                        : undefined
                    }
                    label={`${i18n('patients.dateOfBirth', 'crs')}*`}
                    onChange={(value) => {
                      formik.setFieldValue('dateOfBirth', value);
                    }}
                  />
                </GridItem>
              </GridSection>
              <GridSection>
                <GridItem>
                  <FormControl fullWidth>
                    <InputLabel id="gender-label">{`${i18n('patients.gender', 'crs')}*`}</InputLabel>
                    <Select
                      labelId="gender-label"
                      id="gender"
                      name="gender"
                      placeholder={`${i18n('patients.gender', 'crs')}*`}
                      value={formik.values.gender}
                      error={formik.touched.gender && !!formik.errors.gender}
                      label={`${i18n('patients.gender', 'crs')}*`}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                      <MenuItem value="unknown">Unknown</MenuItem>
                    </Select>
                    {formik.touched.gender && !!formik.errors.gender ? (
                      <FormHelperText error>{formik.errors.gender}</FormHelperText>
                    ) : null}
                  </FormControl>
                </GridItem>
                <GridItem>
                  <GenderIdentityAutoComplete
                    value={formik.values.genderIdentity}
                    onValueChange={(gender) => {
                      formik.setFieldValue('genderIdentity', gender);
                    }}
                    genderIdentityVS={genderIdentityVS}
                    isLoading={isLoadingVS}
                  />
                </GridItem>
              </GridSection>
              <GridSection>
                <GridItem xs={12}>
                  <Typography variant="body2" fontWeight={'bold'}>
                    {i18n('patients.addPatient.contactDetails', 'crs')}
                  </Typography>
                </GridItem>
              </GridSection>
              <GridSection spacing={2}>
                <GridItem>
                  <FormControl fullWidth>
                    <InputLabel id="email-type-label">
                      {i18n('patients.emailType', 'crs')}
                    </InputLabel>
                    <Select
                      labelId="email-type-label"
                      id="emailType"
                      name="emailType"
                      value={formik.values.emailType}
                      placeholder={i18n('patients.emailType', 'crs')}
                      label={i18n('patients.emailType', 'crs')}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="home">Home</MenuItem>
                      <MenuItem value="work">Work</MenuItem>
                    </Select>
                    {formik.touched.emailType && !!formik.errors.emailType ? (
                      <FormHelperText error>{formik.errors.emailType}</FormHelperText>
                    ) : null}
                  </FormControl>
                </GridItem>
                <GridItem>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label={i18n('patients.email', 'crs')}
                    placeholder={i18n('patients.email', 'crs')}
                    error={formik.touched.email && !!formik.errors.email}
                    helperText={formik.touched.email && formik.errors.email}
                    variant="outlined"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                  />
                </GridItem>
              </GridSection>
              <GridSection>
                <GridItem xs={2.8}>
                  <FormControl fullWidth>
                    <InputLabel id="phone-type-label">
                      {i18n('patients.phoneType', 'crs')}
                    </InputLabel>
                    <Select
                      labelId="phone-type-label"
                      id="phoneType"
                      name="phoneType"
                      value={formik.values.phoneType}
                      placeholder={i18n('patients.phoneType', 'crs')}
                      label={i18n('patients.phoneType', 'crs')}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="home">Home</MenuItem>
                      <MenuItem value="work">Work</MenuItem>
                      <MenuItem value="mobile">Mobile</MenuItem>
                    </Select>
                    {formik.touched.phoneType && !!formik.errors.phoneType ? (
                      <FormHelperText error>{formik.errors.phoneType}</FormHelperText>
                    ) : null}
                  </FormControl>
                </GridItem>
                <GridItem xs={2.8}>
                  <InputMask
                    mask="(999)"
                    value={formik.values.areaCode}
                    onChange={(event) => {
                      formik.setFieldValue('areaCode', event.target.value);
                    }}
                  >
                    {(inputProps: any) => (
                      <TextField
                        fullWidth
                        label={i18n('patients.areaCode', 'crs')}
                        placeholder={i18n('patients.areaCode', 'crs')}
                        error={formik.touched.areaCode && !!formik.errors.areaCode}
                        helperText={formik.touched.areaCode && formik.errors.areaCode}
                        variant="outlined"
                        {...inputProps}
                      />
                    )}
                  </InputMask>
                </GridItem>
                <GridItem xs={6}>
                  <InputMask
                    mask="999-9999"
                    value={formik.values.phone}
                    onChange={(event) => {
                      formik.setFieldValue('phone', event.target.value);
                    }}
                  >
                    {(inputProps: any) => (
                      <TextField
                        fullWidth
                        label={i18n('patients.phone', 'crs')}
                        placeholder={i18n('patients.phone', 'crs')}
                        error={formik.touched.phone && !!formik.errors.phone}
                        helperText={formik.touched.phone && formik.errors.phone}
                        variant="outlined"
                        {...inputProps}
                      />
                    )}
                  </InputMask>
                </GridItem>
              </GridSection>
              <GridSection sx={{ alignItems: 'center' }}>
                <GridItem xs={6}>
                  <Typography variant="body2" fontWeight={'bold'}>
                    {i18n('patients.details.addressDetails.title', 'crs')}
                  </Typography>
                </GridItem>
                <GridItem xs={3}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-end',
                      width: '100%',
                    }}
                  >
                    <Button
                      color="primary"
                      startIcon={<Add />}
                      onClick={() => setOpenAddAddress(true)}
                      variant='contained'
                    >
                      {i18n('patients.details.addressDetails.add', 'crs')}
                    </Button>
                  </Box>
                </GridItem>
              </GridSection>
              <GridSection>
                <GridItem>
                  <TextField
                    name="street"
                    label={i18n('patients.details.addressDetails.streetOne', 'crs')}
                    value={formik.values.street}
                    fullWidth
                    disabled
                  />
                </GridItem>
                <GridItem>
                  <TextField
                    name="street2"
                    label={i18n('patients.details.addressDetails.streetTwo', 'crs')}
                    value={formik.values.street2}
                    fullWidth
                    disabled
                  />
                </GridItem>
              </GridSection>
              <GridSection>
                <GridItem>
                  <TextField
                    name="city"
                    label={i18n('patients.details.addressDetails.city', 'crs')}
                    value={formik.values.city}
                    fullWidth
                    disabled
                  />
                </GridItem>
                <GridItem>
                  <TextField
                    name="district"
                    label={i18n('patients.details.addressDetails.county', 'crs')}
                    value={formik.values.district}
                    fullWidth
                    disabled
                  />
                </GridItem>
              </GridSection>
              <GridSection>
                <GridItem xs={isAllowedAddressHousingStatus ? 3.9 : 6}>
                  <TextField
                    name="state"
                    label={i18n('patients.details.addressDetails.state', 'crs')}
                    value={formik.values.state}
                    fullWidth
                    disabled
                  />
                </GridItem>
                <GridItem xs={isAllowedAddressHousingStatus ? 3.9 : 6}>
                  <TextField
                    name="zipCode"
                    label={i18n('patients.details.addressDetails.zipCode', 'crs')}
                    value={formik.values.zipCode}
                    fullWidth
                    disabled
                  />
                </GridItem>
                {isAllowedAddressHousingStatus && (
                  <GridItem xs={3.9}>
                    <TextField
                      name="housingStatus"
                      label={i18n('patients.details.addressDetails.housingStatus', 'crs')}
                      value={formik.values.housingStatus ? 
                        capitalize(formik.values.housingStatus?.replaceAll("-", " ")) : ''
                      }
                      fullWidth
                      disabled
                    />
                  </GridItem>
                )}
              </GridSection>
              <GridSection>
                <GridItem sx={{ position: 'absolute' }}>
                  <Button color="primary" variant="outlined" onClick={() => formik.resetForm()}>
                    Clear All
                  </Button>
                </GridItem>
              </GridSection>
            </Grid>
          </Grid>
        </Box>
      </CustomModal>
      <Dialog open={openAddAddress} fullWidth maxWidth="md">
        <DialogTitle> Add Address</DialogTitle>
        <Card sx={{ p: 2, mt: 2 }}>
          <AddAddressLoading 
            isEditable={true}
            handleClose={handlerClose} 
            handleSave={setAddressFields}
            externalError={externalError}
            {...(isAllowedAddressHousingStatus ? { 
              childrenTop: 
                <ListItem>
                  <FormControl fullWidth error={externalError}>
                    <InputLabel id="address-housing-status-label">
                      Housing Status {patientConfig?.address?.requiredHousingStatus ? "*" : ""}
                    </InputLabel>
                    <Select
                      labelId="address-housing-status-label"
                      id="addressHousingStatus"
                      name="addressHousingStatus"
                      label={`Housing Status ${patientConfig?.address?.requiredHousingStatus ? "*" : ""}`}
                      value={formik.values.housingStatus ?? ""}
                      onChange={(ev) => {
                        formik.setFieldValue('housingStatus', String(ev.target.value));
                        setExternalError(false);
                      }}
                    >
                      {housingStatusVS?.asList().map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                          {capitalize(option?.code?.replaceAll("-", " "))}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </ListItem>
              } : {}
            )}
          />
        </Card>
      </Dialog>
    </>
  );
};

export default AddPatientModal;
